import { React, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';

import { ReactComponent as LinkedinIcon } from '../../../../assets/images/ColoredLinkedlnIconImage.svg';
import { ReactComponent as PersonIcon } from '../../../../assets/images/PersonIcon.svg';
import ContactSources from '../../../../components/ContactSources/ContactSources';
import Button from '../../../../components/shared/Button/Button';
import CustomStarField from '../../../../components/shared/CustomStarField/CustomStarField';
import CustomTextField from '../../../../components/shared/CustomTextField/CustomTextField';
import LocationSelect from '../../../../components/shared/Location/LocationSelect';
import Modal from '../../../../components/shared/Modal/Modal';
import { FORM_CONSTANTS, FORM_MESSAGES, LINKEDIN_REGEX, WEBSITE_REGEX } from '../../../../constants/commonConstants';
import { IsMobile } from '../../../../helpers/IsMobile';

import ContactFormCustomFieldMobile from './ContactFormCustomFields/ContactFormCustomFieldMobile';
import ContactFormCustomFieldWeb from './ContactFormCustomFields/ContactFormCustomFieldWeb';
import ContactFormEmailMobile from './ContactFormEmails/ContactFormEmailMobile';
import ContactFormEmailWeb from './ContactFormEmails/ContactFormEmailWeb';
import ContactFormPhoneNumberMobile from './ContactFormPhoneNumbers/ContactFormPhoneNumberMobile';
import ContactFormPhoneNumberWeb from './ContactFormPhoneNumbers/ContactFormPhoneNumberWeb';
import ContactFormTags from './ContactFormTags/ContactFormTags';
import ContactFormWebsites from './ContactFormWebsites/ContactFormWebsites';

import styles from './AddContactModal.module.scss';

const ContactFormSchema = Yup.object().shape({
  first_name: Yup.string()
    .min(FORM_CONSTANTS.MIN_LENGTH, FORM_MESSAGES.TOO_SHORT)
    .max(FORM_CONSTANTS.MAX_LENGTH, FORM_MESSAGES.TOO_LONG)
    .required('Required'),
  last_name: Yup.string()
    .min(FORM_CONSTANTS.MIN_LENGTH, FORM_MESSAGES.TOO_SHORT)
    .max(FORM_CONSTANTS.MAX_LENGTH, FORM_MESSAGES.TOO_LONG)
    .required('Required'),
  company: Yup.string()
    .nullable(true)
    .min(FORM_CONSTANTS.MIN_LENGTH, FORM_MESSAGES.TOO_SHORT)
    .max(FORM_CONSTANTS.MAX_LENGTH, FORM_MESSAGES.TOO_LONG),
  job_title: Yup.string()
    .nullable(true)
    .min(FORM_CONSTANTS.MIN_LENGTH, FORM_MESSAGES.TOO_SHORT)
    .max(FORM_CONSTANTS.MAX_LENGTH, FORM_MESSAGES.TOO_LONG),
  linkedin_url: Yup.string().matches(LINKEDIN_REGEX).nullable(true),
  location: Yup.string().nullable(true),
  user_contact_emails_attributes: Yup.array()
    .of(
      Yup.object().shape({
        email: Yup.string().email().required(),
      })
    )
    .test('unique', 'Emails must be unique', (value) => {
      const emails = value.map((val) => val.email);
      const result = new Set(emails).size === value.length;
      return result;
    }),
  user_contact_websites_attributes: Yup.array()
    .nullable(true)
    .of(
      Yup.object().shape({
        website: Yup.string().matches(WEBSITE_REGEX),
      })
    )
    .test('unique', 'Websites must be unique', (value) => {
      const websites = value.map((val) => val.website);
      const result = new Set(websites).size === value.length;
      return result;
    }),
  user_contact_tags_attrbutes: Yup.array()
    .nullable(true)
    .of(
      Yup.object().shape({
        general_tags_attributes: Yup.object().shape({ name: Yup.string() }),
      })
    ),
  user_contact_phone_numbers_attributes: Yup.array()
    .nullable(true)
    .of(
      Yup.object().shape({
        number_type: Yup.string(),
        phone_numbers_attribute: Yup.object().shape({ full_number: Yup.string() }),
      })
    )
    .test('unique', 'Phone number must be unique', async (value) => {
      const phoneNumber = value.map(({ phone_numbers_attributes }) => phone_numbers_attributes.full_number);
      const result = new Set(phoneNumber).size === value.length;
      return result;
    }),
  custom_fields: Yup.array()
    .nullable(true)
    .of(
      Yup.object().shape({
        custom_field: Yup.string(),
        label: Yup.string(),
      })
    )
    .test('unique', 'Custom Fields must be unique', async (value) => {
      const customFields = value.map((custom_fields) => custom_fields.custom_field);
      const result = new Set(customFields).size === value.length;
      return result;
    }),
});

const INITIAL_VALUES = {
  first_name: '',
  last_name: '',
  full_name: '',
  star: false,
  company: '',
  job_title: '',
  linkedin_url: '',
  location: '',
  user_contact_emails_attributes: [{ email: '', email_type: '' }],
  user_contact_websites_attributes: [{ website: '' }],
  user_contact_phone_numbers_attributes: [{ number_type: '', phone_numbers_attributes: { full_number: '' } }],
  taggings_attributes: [],
  custom_fields: [{ custom_field: '', label: '' }],
};

const AddContactModal = ({
  showModal,
  setShowModal,
  formData,
  handleContactUpdate,
  handleContactCreate,
  toggleDeleteOptionModal,
}) => {
  const { t } = useTranslation('common');
  const [isEdit, setIsEdit] = useState(false);
  const isMobile = IsMobile();

  useEffect(() => {
    if (Object.keys(formData).length) {
      setIsEdit(true);
    } else {
      setIsEdit(false);
    }
  }, [formData]);

  const handleSubmit = (data) => {
    const newData = { ...data };

    newData.full_name = newData.first_name.concat(' ', newData.last_name);

    if (
      newData.user_contact_emails_attributes?.length > 0 &&
      newData.user_contact_emails_attributes[0].email.length === 0
    ) {
      newData.user_contact_emails_attributes = null;
    }
    if (newData.user_contact_phone_numbers_attributes?.length) {
      const newPhone = newData.user_contact_phone_numbers_attributes.filter(
        ({ phone_numbers_attributes }) => phone_numbers_attributes.full_number
      );
      if (newPhone.length) {
        newData.user_contact_phone_numbers_attributes = newPhone;
      } else {
        newData.user_contact_phone_numbers_attributes = null;
      }
    }
    if (
      newData.taggings_attributes?.length > 0 &&
      newData.taggings_attributes[0].general_tags_attributes.name.length === 0
    ) {
      newData.taggings_attributes = null;
    }
    if (newData.user_contact_websites_attributes?.length > 0) {
      const newWebsites = newData.user_contact_websites_attributes.filter(({ website }) => website);
      if (newWebsites.length) {
        newData.user_contact_websites_attributes = newWebsites;
      } else {
        newData.user_contact_websites_attributes = null;
      }
    }
    if (newData.custom_fields?.length > 0) {
      const newCustomField = newData.custom_fields.filter(({ custom_field }) => custom_field);
      if (newCustomField.length) {
        newData.custom_fields = newCustomField;
      } else {
        newData.custom_fields = null;
      }
    }

    if (isEdit) {
      handleContactUpdate(newData);
    } else {
      handleContactCreate(newData);
    }
  };

  const handleDelete = useCallback(() => {
    setShowModal(false);
    if (isEdit) {
      toggleDeleteOptionModal(formData);
    }
  }, [isEdit, formData]);

  return (
    <Modal
      show={showModal}
      onHide={() => setShowModal(false)}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      fullscreen={isMobile}
    >
      <Modal.CustomHeader closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          {isEdit ? t('contactModal.editContact') : t('contactModal.addNewContact')}
        </Modal.Title>
      </Modal.CustomHeader>
      <Modal.Body>
        <Formik
          initialValues={isEdit ? formData : INITIAL_VALUES}
          onSubmit={handleSubmit}
          enableReinitialize
          validationSchema={ContactFormSchema}
        >
          {({ values, setValues, dirty, isValid }) => (
            <Form>
              <div className={isMobile && styles.phoneContainer}>
                <div className={styles.container}>
                  <div className={styles.fullNameContainer}>
                    <div className={isMobile ? styles.multiInputFieldMobile : styles.inputField}>
                      <Field
                        name="first_name"
                        as={CustomTextField}
                        type="text"
                        label={t('formLabels.firstName')}
                        CustomIcon={PersonIcon}
                      />
                      <Field
                        name="last_name"
                        as={CustomTextField}
                        type="text"
                        label={t('formLabels.lastName')}
                        CustomIcon={PersonIcon}
                      />
                    </div>
                    <div>
                      <Field
                        as={CustomStarField}
                        name="star"
                        checked={values.star}
                        handleChange={() => setValues({ ...values, star: !values.star })}
                      />
                    </div>
                  </div>
                  <div className={isMobile ? styles.multiInputFieldMobile : styles.inputField}>
                    <Field name="company" as={CustomTextField} type="text" label={t('contactModal.company')} />
                  </div>
                  <div className={isMobile ? styles.multiInputFieldMobile : styles.inputField}>
                    <Field
                      name="job_title"
                      as={CustomTextField}
                      type="text"
                      label={t('contactModal.title')}
                      className={styles.inputField}
                    />
                  </div>
                  {isMobile ? <ContactFormEmailMobile isEdit={isEdit} /> : <ContactFormEmailWeb isEdit={isEdit} />}
                  {isMobile ? (
                    <ContactFormPhoneNumberMobile isEdit={isEdit} />
                  ) : (
                    <ContactFormPhoneNumberWeb isEdit={isEdit} />
                  )}
                  <div className={isMobile ? styles.multiInputFieldMobile : styles.inputField}>
                    <Field
                      name="linkedin_url"
                      as={CustomTextField}
                      type="text"
                      label={t('labels.linkedinUrl')}
                      className={styles.inputField}
                      CustomIcon={LinkedinIcon}
                    />
                  </div>
                  <ContactFormWebsites isEdit={isEdit} />
                  <div className={isMobile ? styles.multiInputFieldMobile : styles.inputField}>
                    <Field
                      name="location"
                      as={LocationSelect}
                      type="text"
                      label={t('labels.location')}
                      onChange={(location) => {
                        setValues({
                          ...values,
                          location: location?.full_address,
                        });
                      }}
                      value={{ full_address: values.location }}
                    />
                  </div>
                  {isMobile ? <ContactFormCustomFieldMobile /> : <ContactFormCustomFieldWeb />}
                  <div className={isMobile ? styles.multiInputFieldMobile : styles.inputField}>
                    <ContactFormTags showModal={showModal} />
                  </div>
                  {isEdit && (
                    <div className={styles.contactSourceContainer}>
                      {t('contactModal.contactSource')}
                      <ContactSources sources={formData.source} />
                    </div>
                  )}
                </div>
                <div>
                  <div className={isMobile ? styles.mobileButtonCotainer : styles.buttonContainer}>
                    <Button
                      variant="outline-secondary-light"
                      type="button"
                      className={isMobile ? styles.phoneButton : styles.button}
                      onClick={handleDelete}
                    >
                      {isEdit ? t('delete') : t('cancel')}
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                      className={isMobile ? styles.phoneButton : styles.button}
                      disabled={!(isValid && dirty)}
                    >
                      {t('save')}
                    </Button>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default AddContactModal;
